import React, { useCallback, useEffect, useState } from "react";
import { ChevronLeft, Download, Trash2, Archive, RefreshCw, Loader2, AlertTriangle, Info, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { parseApiError } from "../../lib/errors";
import { ukDate, ukDateTime } from "../../lib/format";
import { Card, PrimaryButton, QuietButton } from "../UI";
import { StatusChip, CountChips } from "./ImportBatchList";
import { ImportRowsTable } from "./ImportRowsTable";
import { ImportUndoDialog } from "./ImportUndoDialog";
import { ImportRowEditDialog } from "./ImportRowEditDialog";
import { downloadThroughApi } from "./importShared";

const PAGE_SIZE = 50;

/* LT-19b §11 — the detail view: counts, paged row table, downloads, Apply. */
export function ImportBatchDetail({ batchId, onBack, onChanged }) {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("");
  const [writtenFilter, setWrittenFilter] = useState("");
  const [busy, setBusy] = useState(null);
  const [notice, setNotice] = useState(null);      // {tone, message, changed?}
  const [undoPlan, setUndoPlan] = useState(null);   // LT-19c — the server's plan, shown before confirm
  const [editRow, setEditRow] = useState(null);     // LT-19d — the row open in the edit dialog
  const [fixing, setFixing] = useState(null);       // LT-19d — row_number whose one-click fix is running

  const load = useCallback(() => {
    const params = { page, page_size: PAGE_SIZE };
    if (filter) params.action = filter;
    if (writtenFilter) params.written = writtenFilter;
    return api.get(`/material-imports/${batchId}`, { params })
      .then((r) => setData(r.data))
      .catch((e) => setNotice({ tone: "error", message: parseApiError(e).message }));
  }, [batchId, page, filter, writtenFilter]);
  useEffect(() => { load(); }, [load]);

  const b = data?.batch;
  const act = async (label, fn) => {
    setBusy(label); setNotice(null);
    try {
      await fn();
      await load();
      onChanged?.();
    } catch (e) {
      const p = parseApiError(e);
      const changed = e?.response?.data?.detail?.changed;
      setNotice({ tone: "error", message: p.message, changed });
      await load();
    } finally { setBusy(null); }
  };

  const apply = () => act("apply", async () => {
    const r = await api.post(`/material-imports/${batchId}/apply`);
    const c = r.data?.batch?.counts || {};
    toast.success(`Applied — ${c.create ?? 0} created, ${c.update ?? 0} updated, ${c.unchanged ?? 0} unchanged, ${c.reject ?? 0} rejected`);
    setNotice({ tone: "ok", message: `Applied ${ukDateTime(r.data?.batch?.applied_at)}. ${r.data?.batch?.cascade_library_items ?? 0} library item(s) flagged for re-pricing.` });
  });
  const refresh = () => act("refresh", async () => { await api.post(`/material-imports/${batchId}/refresh`); setNotice({ tone: "ok", message: "Preview re-resolved against the library as it is now." }); });
  const discard = () => act("discard", () => api.post(`/material-imports/${batchId}/discard`));
  const archive = () => act("archive", () => api.post(`/material-imports/${batchId}/archive`));
  const dlOriginal = () => act("dl", () => downloadThroughApi(`/material-imports/${batchId}/file`, b.filename));
  const dlRejected = () => act("dlr", async () => {
    const note = await downloadThroughApi(`/material-imports/${batchId}/rejected.csv`, b.filename.replace(/\.csv$/i, "") + "-rejected.csv");
    if (note) setNotice({ tone: "info", message: note });
  });
  // LT-19d — the whole list with the preview edits applied: the next template.
  const dlRevised = () => act("dlv", async () => {
    const note = await downloadThroughApi(`/material-imports/${batchId}/revised.csv`, b.filename.replace(/\.csv$/i, "") + "-revised.csv");
    if (note) setNotice({ tone: "info", message: `Revised CSV downloaded — ${note}. Use it as the next upload so these edits are not needed again.` });
  });
  // LT-19d/e — one-click remedies offered by the server on a rejected row.
  const [lookups, setLookups] = useState(null);     // units + categories for the alias fix
  const canApply = data?.batch?.status === "draft" || data?.batch?.status === "failed";
  useEffect(() => {
    if (!canApply || lookups) return;
    Promise.all([api.get("/material-units"), api.get("/material-categories")])
      .then(([u, c]) => setLookups({ units: u.data || [], categories: c.data || [] }))
      .catch(() => setLookups({ units: [], categories: [] }));
  }, [canApply, lookups]);
  const fixRow = async (r, extra = {}) => {
    const f = r.fix;
    if (!f) return;
    setFixing(r.row_number); setNotice(null);
    try {
      const before = data?.batch?.counts?.reject ?? 0;
      if (f.type === "make_unit_divisible") {
        await api.patch(`/material-units/${f.unit_id}`, { is_divisible: true });
        await api.post(`/material-imports/${batchId}/refresh`);
        toast.success(`'${f.unit_label}' is now divisible in Settings — preview re-checked`);
        setLookups(null);
      } else if (f.type === "set_cells") {
        const res = await api.patch(`/material-imports/${batchId}/rows/${r.row_number}`, { cells: f.cells });
        const nr = res.data.row;
        if (nr?.action === "reject") toast.warning(`Row ${r.row_number} re-checked — still rejected: ${nr.reject_reason}`);
        else toast.success(`Row ${r.row_number} re-checked → ${(nr?.action || "").toUpperCase()}`);
      } else if (f.type === "add_alias") {
        const ep = f.kind === "unit" ? "/material-units" : "/material-categories";
        const saved = await api.post(`${ep}/${extra.target_id}/aliases`, { alias: f.value });
        const d = await api.post(`/material-imports/${batchId}/refresh`);
        const fixed = before - (d.data?.batch?.counts?.reject ?? before);
        const already = (saved.data.aliases || []).every((a) => a.trim().toLowerCase() !== f.value.trim().toLowerCase());
        toast.success(already
          ? `'${saved.data.label}' is in Settings — preview re-checked, ${fixed} row${fixed === 1 ? "" : "s"} fixed`
          : `'${f.value}' now reads as '${saved.data.label}' (saved in Settings) — ${fixed} row${fixed === 1 ? "" : "s"} fixed`);
        setLookups(null);
      }
      await load();
      onChanged?.();
    } catch (e) {
      const msg = parseApiError(e).message;
      toast.error(msg);
      setNotice({ tone: "error", message: msg });
    } finally { setFixing(null); }
  };
  // Settings changed after this preview was checked (a category added, a unit
  // made divisible …)? Say so — the rows still show the old rejects until re-checked.
  const settingsChangedAfter = React.useMemo(() => {
    if (!canApply || !lookups || !data?.rows?.length) return false;
    const resolved = data.rows.reduce((mx, r) => (r.resolved_at > mx ? r.resolved_at : mx), "");
    return [...lookups.units, ...lookups.categories].some((x) => (x.updated_at || x.created_at || "") > resolved);
  }, [canApply, lookups, data]);
  // LT-19c — undo: fetch the plan, show it, confirm, then post with the plan's hash.
  const openUndo = () => act("plan", async () => {
    const r = await api.get(`/material-imports/${batchId}/rollback-plan`);
    setUndoPlan(r.data);
  });
  const confirmUndo = () => act("undo", async () => {
    try {
      const r = await api.post(`/material-imports/${batchId}/rollback`, { plan_hash: undoPlan.plan_hash });
      const s = r.data?.batch?.rollback_summary || {};
      setUndoPlan(null);
      toast.success(`Undone — ${s.prices_back ?? 0} price(s) walked back, ${s.fields_back ?? 0} field(s) put back, ${s.archived ?? 0} archived`);
      setNotice({ tone: "ok", message: `Import undone ${ukDateTime(r.data?.batch?.rolled_back_at)}. ${s.cascade_library_items ?? 0} library item(s) flagged for re-pricing.` });
    } catch (e) {
      if (e?.response?.status === 409 && e.response.data?.detail?.plan) {
        setUndoPlan({ ...e.response.data.detail, batch: undoPlan.batch });     // the plan moved — show the fresh one
        toast.warning("The library moved under the plan — review the refreshed plan.");
        return;
      }
      setUndoPlan(null);
      throw e;
    }
  });

  if (!b) return <div className="text-ink-4 py-6" style={{ fontSize: 12.5 }} data-testid="import-detail-loading">{notice?.message || "Loading…"}</div>;
  const canUndo = b.status === "applied" || b.status === "rollback_failed";
  const showWritten = ["failed", "applied", "rolled_back", "rollback_failed"].includes(b.status);
  return (
    <div className="space-y-4" data-testid="import-detail">
      <ImportUndoDialog plan={undoPlan} open={!!undoPlan} onCancel={() => setUndoPlan(null)} onConfirm={confirmUndo} busy={busy === "undo"} />
      <ImportRowEditDialog batchId={batchId} row={editRow} open={!!editRow} onClose={() => setEditRow(null)}
        onSaved={async () => { await load(); onChanged?.(); }} />
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <QuietButton icon={ChevronLeft} onClick={onBack} data-testid="import-detail-back">All imports</QuietButton>
          <h3 className="font-display text-ink truncate" style={{ fontSize: 15, fontWeight: 600 }} data-testid="import-detail-filename">{b.filename}</h3>
          <StatusChip status={b.status} superseded={b.superseded} />
        </div>
        <div className="flex items-center gap-1 whitespace-nowrap">
          <QuietButton icon={Download} onClick={dlOriginal} disabled={!!busy} data-testid="import-detail-download-original">Original file</QuietButton>
          {(b.counts?.reject ?? 0) > 0 && (
            <QuietButton icon={Download} onClick={dlRejected} disabled={!!busy} data-testid="import-detail-download-rejected">Rejected rows (CSV)</QuietButton>
          )}
          {(b.edited_rows ?? 0) > 0 && (
            <QuietButton icon={Download} onClick={dlRevised} disabled={!!busy} data-testid="import-detail-download-revised">Revised file (CSV)</QuietButton>
          )}
          {canApply && <QuietButton icon={busy === "refresh" ? Loader2 : RefreshCw} onClick={refresh} disabled={!!busy} data-testid="import-detail-refresh">Re-check preview</QuietButton>}
          {canApply && <QuietButton icon={Trash2} onClick={discard} disabled={!!busy} data-testid="import-detail-discard">Discard</QuietButton>}
          {canUndo && (
            <QuietButton icon={busy === "plan" ? Loader2 : Undo2} onClick={openUndo} disabled={!!busy} data-testid="import-detail-undo">
              {b.status === "rollback_failed" ? "Resume undo" : "Undo this import"}
            </QuietButton>
          )}
          {!canApply && !b.archived_at && <QuietButton icon={Archive} onClick={archive} disabled={!!busy} data-testid="import-detail-archive">Archive</QuietButton>}
          {canApply && (
            <PrimaryButton icon={busy === "apply" ? Loader2 : undefined} onClick={apply} disabled={!!busy} data-testid="import-detail-apply">
              {b.status === "failed" ? "Resume apply" : "Apply"}
            </PrimaryButton>
          )}
        </div>
      </div>

      <Card className="p-4 space-y-2">
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-ink-3" style={{ fontSize: 12 }} data-testid="import-detail-meta">
          <span>Supplier <b className="text-ink font-medium">{b.supplier_label}</b></span>
          <span>Effective from <b className="text-ink font-medium">{ukDate(b.effective_from)}</b></span>
          <span>Uploaded {ukDateTime(b.uploaded_at)}</span>
          {b.applied_at && <span>Applied {ukDateTime(b.applied_at)}</span>}
          <span className="font-mono" style={{ fontSize: 10.5 }}>sha256 {String(b.file_hash).slice(0, 12)}…</span>
        </div>
        <div><CountChips counts={b.counts} /> <span className="text-ink-4" style={{ fontSize: 11 }}>· {b.row_count} data row{b.row_count === 1 ? "" : "s"}</span></div>
        {b.status === "draft" && (
          <div className="text-ink-3" style={{ fontSize: 12 }} data-testid="import-detail-draft-note">
            Nothing has been written. Apply re-checks every row against the library first and refuses if the preview has changed.
            Prices apply as soon as you import — the effective date only records when the supplier's list was issued.
            {" "}Any row can be <b className="text-ink font-medium">edited</b> here before you apply — the file stays as uploaded, the edits ride on this import.
          </div>
        )}
        {(b.edited_rows ?? 0) > 0 && (
          <div className="rounded border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2 flex gap-2" style={{ fontSize: 12 }} data-testid="import-detail-edited-note">
            <Info size={14} className="shrink-0 mt-0.5" />
            <span>
              <b>{b.edited_rows} row{b.edited_rows === 1 ? "" : "s"} edited in the preview.</b> The original file is untouched;
              {" "}{b.status === "draft" || b.status === "failed" ? "Apply writes the edited values." : "the edited values were what got written."}
              {" "}Download the <b>Revised file (CSV)</b> and use it next time so the same edits aren't needed again.
            </span>
          </div>
        )}
        {settingsChangedAfter && (
          <div className="rounded border border-sky-200 bg-sky-50 text-sky-900 px-3 py-2 flex items-center gap-2" style={{ fontSize: 12 }} data-testid="import-detail-stale-note">
            <Info size={14} className="shrink-0" />
            <span className="flex-1">Settings → Materials changed after this preview was checked (a category or unit added or edited). The rows below still show the old result until you re-check.</span>
            <button type="button" onClick={refresh} disabled={!!busy}
              className="inline-flex items-center gap-1 px-2 py-1 rounded border border-sky-300 bg-white hover:bg-sky-100 disabled:opacity-50"
              data-testid="import-detail-stale-recheck">
              {busy === "refresh" ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Re-check preview
            </button>
          </div>
        )}
        {b.duplicate_of && (
          <div className="rounded border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 flex gap-2" style={{ fontSize: 12 }} data-testid="import-detail-duplicate-warning">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <span>This exact file was already applied as batch <span className="font-mono">{String(b.duplicate_of).slice(0, 8)}</span>. Re-running it is allowed — the preview shows what would still change.</span>
          </div>
        )}
        {b.unknown_columns?.length > 0 && (
          <div className="text-amber-800" style={{ fontSize: 12 }} data-testid="import-detail-unknown-columns">
            Ignored column{b.unknown_columns.length === 1 ? "" : "s"} (not in the contract): <span className="font-mono">{b.unknown_columns.join(", ")}</span>
          </div>
        )}
        {b.status === "failed" && (
          <div className="rounded border border-red-200 bg-red-50 text-red-900 px-3 py-2" style={{ fontSize: 12 }} data-testid="import-detail-failed-note">
            <b>Stopped partway:</b> {b.error} — {data.written_rows} of {b.row_count} row(s) wrote. Filter to “unwritten rows” to see what Resume will write.
          </div>
        )}
        {b.status === "applied" && data.written_rows != null && (
          <div className="text-ink-4" style={{ fontSize: 11.5 }} data-testid="import-detail-written">{data.written_rows} row(s) stamped as written{b.cascade_library_items != null ? ` · ${b.cascade_library_items} library item(s) flagged for re-pricing` : ""}</div>
        )}
        {b.status === "rolled_back" && b.rollback_summary && (
          <div className="rounded border border-sky-200 bg-sky-50 text-sky-900 px-3 py-2" style={{ fontSize: 12 }} data-testid="import-detail-rolled-back-note">
            <b>Undone {ukDateTime(b.rolled_back_at)}:</b> {b.rollback_summary.prices_back ?? 0} price(s) walked back, {b.rollback_summary.fields_back ?? 0} other field(s) put back,
            {" "}{b.rollback_summary.archived ?? 0} created material(s) archived, {(b.rollback_summary.left_alone ?? 0) + (b.rollback_summary.partial ?? 0)} row(s) changed since and left alone.
            {b.rollback_summary.legacy ? " Applied before undo existed — prices only." : ""} The import's history lines stay; each price got a new <i>import rollback</i> line.
          </div>
        )}
        {b.status === "rollback_failed" && (
          <div className="rounded border border-red-200 bg-red-50 text-red-900 px-3 py-2" style={{ fontSize: 12 }} data-testid="import-detail-rollback-failed-note">
            <b>Undo stopped partway:</b> {b.error} — {b.rollback_summary?.rows ?? 0} row(s) walked back so far. “Resume undo” finishes the rest; rows already done are not touched again.
          </div>
        )}
        {notice && (
          <div className={"rounded border px-3 py-2 flex gap-2 " + (notice.tone === "error" ? "border-red-200 bg-red-50 text-red-900" : notice.tone === "ok" ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-line bg-surface-2/40 text-ink-2")}
               style={{ fontSize: 12 }} data-testid="import-detail-notice">
            <Info size={14} className="shrink-0 mt-0.5" />
            <div>
              <div>{notice.message}</div>
              {notice.changed?.length > 0 && (
                <ul className="mt-1 font-mono" style={{ fontSize: 11 }} data-testid="import-detail-drift">
                  {notice.changed.map((c) => (
                    <li key={c.row_number}>row {c.row_number}: {c.was.action}{c.was.reject_reason ? ` (${c.was.reject_reason})` : ""} → {c.now.action}{c.now.reject_reason ? ` (${c.now.reject_reason})` : ""}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </Card>

      <ImportRowsTable rows={data.rows} total={data.total} page={data.page} pageSize={PAGE_SIZE}
        onPage={setPage} filter={filter} onFilter={(f) => { setFilter(f); setPage(1); }}
        showWritten={showWritten} writtenFilter={writtenFilter}
        showUndo={b.status === "rolled_back" || b.status === "rollback_failed"}
        editable={canApply} onEdit={setEditRow} onFix={fixRow} fixing={fixing} lookups={lookups}
        onWrittenFilter={(v) => { setWrittenFilter(v); setPage(1); }} />
    </div>
  );
}
